import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Clock, FileText, BookOpen, ChevronRight } from "lucide-react";
import { api, API } from "@/lib/api";
import { Reveal, SectionLabel, FramedImage, ArticleRenderer } from "@/components/collection";
import { formatCitation, extractToc } from "@/lib/article";

const STATUS_STYLE = {
  "Research in Progress": "border-[hsl(43,74%,52%)] text-[hsl(43,74%,52%)]",
  "Peer Review Requested": "border-[hsl(174,40%,40%)] text-[hsl(174,40%,40%)]",
  "Evidence Complete": "border-[hsl(145,50%,35%)] text-[hsl(145,50%,35%)]",
  "Living Document": "border-[hsl(355,76%,33%)] text-[hsl(355,76%,33%)]",
};

const StatusBadge = ({ status, className = "" }) => (
  <span className={`inline-flex items-center border rounded-none px-2.5 py-1 text-[10px] tracking-[0.2em] uppercase font-ui font-semibold bg-transparent ${STATUS_STYLE[status] || STATUS_STYLE["Research in Progress"]} ${className}`}>
    {status}
  </span>
);

const fmtDate = (d) => {
  if (!d) return null;
  try { return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); }
  catch { return d; }
};

const VOLUME_ORDER = [
  "Volume I — The Event",
  "Volume II — The Tickets",
  "Volume III — Production & Design",
  "Volume IV — The Collection",
  "Volume V — The Graceland Exhibition",
  "Volume VI — Preservation & Authentication",
  "Volume VII — Research & Historical Corrections",
  "Volume VIII — Supporting Documents",
];

/* ==================== Studies index ==================== */
export const StudiesIndexPage = () => {
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/studies").then((r) => setStudies(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const grouped = React.useMemo(() => {
    const map = {};
    studies.forEach((s) => {
      const v = s.volume || "Other Studies";
      (map[v] = map[v] || []).push(s);
    });
    const order = [...VOLUME_ORDER, "Other Studies"];
    return Object.keys(map)
      .sort((a, b) => {
        const ia = order.indexOf(a); const ib = order.indexOf(b);
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      })
      .map((v) => ({ volume: v, items: map[v] }));
  }, [studies]);

  const Card = ({ s, idx }) => (
    <Reveal delay={Math.min((idx % 3) * 80, 240)}>
      <Link to={`/studies/${s.slug}`} className="group block bg-white border-l-2 border-[hsl(43,74%,52%)] hover:shadow-lg transition-shadow" data-testid={`study-card-${s.slug}`}>
        <FramedImage src={s.hero_image} alt={s.title} className="aspect-[3/2]" />
        <div className="p-5">
          <div className="flex items-center justify-between gap-2 mb-3">
            <StatusBadge status={s.status} />
            <span className="inline-flex items-center gap-1 text-xs font-ui text-[hsl(0,0%,40%)]"><Clock className="w-3 h-3" /> {s.reading_time} min</span>
          </div>
          <h2 className="font-display text-xl text-[hsl(0,0%,10%)] leading-snug group-hover:text-[hsl(355,76%,33%)] transition-colors">{s.title}</h2>
          {s.subtitle && <p className="mt-2 text-sm font-editorial text-[hsl(0,0%,40%)] leading-relaxed line-clamp-2">{s.subtitle}</p>}
          <p className="mt-3 text-xs font-ui text-[hsl(0,0%,50%)]">{s.citation_count} citation{s.citation_count === 1 ? "" : "s"}{s.published_date ? ` · ${fmtDate(s.published_date)}` : ""}</p>
        </div>
      </Link>
    </Reveal>
  );

  return (
    <div className="reading-surface min-h-[70vh]" data-testid="studies-index-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-14 md:py-20">
        <Reveal>
          <p className="font-ui text-xs uppercase tracking-[0.2em] text-[hsl(355,76%,33%)] mb-4">The Intellectual Center of the Archive</p>
          <h1 className="font-display font-light tracking-tight text-4xl sm:text-5xl text-[hsl(0,0%,10%)] mt-4 mb-4">Historical Studies</h1>
          <p className="text-base md:text-lg font-editorial text-[hsl(0,0%,40%)] leading-relaxed max-w-3xl mb-12">
            A developing reference work on the Elvis Aloha from Hawaii 1973 collection — its tickets, design and
            production history, Graceland exhibition, provenance, preservation, and the correction of repeated myths.
            Studies are organized into volumes.
          </p>
        </Reveal>

        {loading ? (
          <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[hsl(355,76%,33%)]" /></div>
        ) : (
          <div className="space-y-16">
            {grouped.map((g) => (
              <div key={g.volume} data-testid={`volume-${g.volume.replace(/[^a-zA-Z]+/g, "-").toLowerCase()}`}>
                <div className="flex items-center gap-4 mb-8">
                  <h2 className="font-display text-2xl text-[hsl(0,0%,10%)]">{g.volume}</h2>
                  <span className="text-sm font-ui text-[hsl(0,0%,50%)]">{g.items.length}</span>
                  <div className="flex-1 border-t border-[hsl(43,74%,52%,0.3)]" />
                </div>
                <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-3">
                  {g.items.map((s, idx) => <Card key={s.id} s={s} idx={idx} />)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ==================== Study detail ==================== */
export const StudyDetailPage = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/studies/${slug}`).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (data?.study) document.title = `${data.study.title} — Elvis Aloha 1973 Archive`;
    return () => { document.title = "Elvis Aloha 1973 Collection"; };
  }, [data]);

  if (loading) return <div className="flex justify-center py-32 bg-[hsl(42,25%,96%)]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[hsl(355,76%,33%)]" /></div>;
  if (!data?.study) return (
    <div className="max-w-3xl mx-auto px-6 py-24 text-center bg-[hsl(42,25%,96%)]">
      <p className="text-[hsl(0,0%,40%)] mb-4 font-editorial">Study not found.</p>
      <Link to="/studies" className="text-[hsl(355,76%,33%)] hover:underline font-ui">← Back to Historical Studies</Link>
    </div>
  );

  const { study, citations, related_items, related_artifacts, provenance_events, suggested, reading_time } = data;
  const pdfUrl = `${API}/studies/${study.slug}/pdf`;
  const toc = extractToc(study.body_markdown);

  return (
    <div className="reading-surface" data-testid="study-detail-page">
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-10 md:py-16">
        <Link to="/studies" className="inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase font-ui font-semibold text-[hsl(0,0%,40%)] hover:text-[hsl(355,76%,33%)] transition-colors mb-8" data-testid="study-back-link">
          <ArrowLeft className="w-4 h-4" /> Historical Studies
        </Link>

        <p className="font-ui text-xs uppercase tracking-[0.2em] text-[hsl(355,76%,33%)] mb-3">Historical Study</p>
        <h1 className="font-display font-light tracking-tight text-4xl sm:text-5xl text-[hsl(0,0%,10%)] mt-3 mb-3 leading-[1.05]">{study.title}</h1>
        {study.subtitle && <p className="font-editorial text-lg md:text-xl text-[hsl(0,0%,40%)] italic mb-5">{study.subtitle}</p>}

        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 py-4 border-y border-[hsl(43,74%,52%,0.3)] text-sm font-ui text-[hsl(0,0%,40%)] mb-8" data-testid="study-meta">
          <StatusBadge status={study.status} />
          <span>{study.author}</span>
          {study.published_date && <span>Published {fmtDate(study.published_date)}</span>}
          {study.revised_date && <span>Revised {fmtDate(study.revised_date)}</span>}
          <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {reading_time} min read</span>
          <span>{citations.length} citation{citations.length === 1 ? "" : "s"}</span>
          <a href={pdfUrl} className="ml-auto inline-flex items-center gap-2 bg-[hsl(0,0%,10%)] text-white rounded-none px-4 py-2 text-xs tracking-[0.1em] uppercase font-semibold hover:bg-[hsl(355,76%,33%)] transition-colors" data-testid="study-pdf-btn">
            <FileText className="w-4 h-4" /> Download PDF
          </a>
        </div>

        {study.hero_image && <FramedImage src={study.hero_image} alt={study.title} className="aspect-[16/9] mb-8 p-2 md:p-3" />}

        {study.keywords?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8" data-testid="study-keywords">
            {study.keywords.map((k) => (
              <span key={k} className="text-[11px] tracking-[0.12em] uppercase font-ui text-[hsl(0,0%,40%)] border border-[hsl(0,0%,80%)] px-2 py-1">{k}</span>
            ))}
          </div>
        )}

        {study.abstract && (
          <div className="border-l-2 border-[hsl(355,76%,33%)] bg-white pl-6 pr-5 py-4 mb-8">
            <p className="font-ui text-xs uppercase tracking-[0.2em] text-[hsl(355,76%,33%)] mb-2">Abstract</p>
            <p className="text-[15px] md:text-base font-editorial text-[hsl(0,0%,10%)] leading-relaxed">{study.abstract}</p>
          </div>
        )}

        {toc.length > 2 && (
          <nav className="border border-[hsl(0,0%,85%)] bg-white p-5 mb-10" data-testid="study-toc">
            <p className="font-ui text-xs uppercase tracking-[0.2em] text-[hsl(355,76%,33%)] mb-3">Contents</p>
            <ol className="space-y-1.5 text-sm font-ui">
              {toc.map((t) => (
                <li key={t.id} className={t.level === 3 ? "ml-4" : ""}>
                  <a href={`#${t.id}`} className="text-[hsl(0,0%,10%)] hover:text-[hsl(355,76%,33%)] hover:underline">{t.text}</a>
                </li>
              ))}
            </ol>
          </nav>
        )}

        <ArticleRenderer markdown={study.body_markdown} citations={citations} />

        {study.slug === "the-life-of-an-aloha-ticket" && related_items?.length > 0 && (
          <section className="mt-10" data-testid="production-lineage">
            <SectionLabel className="text-[#8C271E]">Production Lineage</SectionLabel>
            <ol className="mt-4 relative border-l-2 border-[#8C271E]/40 ml-2">
              {related_items.map((r) => (
                <li key={r.id} className="relative pl-6 pb-5 last:pb-0">
                  <span className="absolute -left-[7px] top-1.5 w-3 h-3 rounded-full bg-[#8C271E] ring-4 ring-[#F5F4F0]" />
                  <Link to={`/collection/item/${r.slug}`} className="group inline-flex items-center gap-2" data-testid={`lineage-${r.slug}`}>
                    <span className="showcase-serif text-[#1A1918] group-hover:text-[#8C271E] transition-colors">{r.title}</span>
                    <ChevronRight className="w-4 h-4 text-[#9A9790]" />
                  </Link>
                  <span className="block text-xs text-[#5C5A56]">{r.category}</span>
                </li>
              ))}
            </ol>
          </section>
        )}

        {study.evidence_matrix?.length > 0 && (
          <section className="mt-12 pt-8 border-t border-[#D6D3C8]" data-testid="evidence-matrix">
            <SectionLabel className="text-[#8C271E]">Evidence Matrix</SectionLabel>
            <div className="mt-4 overflow-x-auto border border-[#D6D3C8]">
              <table className="w-full text-sm">
                <thead className="bg-[#EAE8E0]">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-semibold text-[#1A1918]">Claim</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-[#1A1918]">Evidence</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-[#1A1918] whitespace-nowrap">Confidence</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#D6D3C8]">
                  {study.evidence_matrix.map((m, i) => (
                    <tr key={i} className="bg-white">
                      <td className="px-4 py-2.5 text-[#1A1918] align-top">{m.claim}</td>
                      <td className="px-4 py-2.5 text-[#5C5A56] align-top">{m.evidence}</td>
                      <td className="px-4 py-2.5 align-top whitespace-nowrap">
                        <span className={`text-[10px] tracking-[0.15em] uppercase font-semibold px-2 py-0.5 border ${
                          m.confidence === "High" ? "border-[#3F6B4C] text-[#3F6B4C]"
                          : m.confidence === "Medium" ? "border-[#B8860B] text-[#B8860B]"
                          : m.confidence === "Open" ? "border-[#8C271E] text-[#8C271E]"
                          : "border-[#9A9790] text-[#5C5A56]"}`}>{m.confidence}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {study.open_questions?.length > 0 && (
          <section className="mt-12 pt-8 border-t border-[#D6D3C8]" data-testid="open-questions">
            <SectionLabel className="text-[#8C271E]">Open Research Questions</SectionLabel>
            <ol className="mt-4 space-y-2 text-[15px] text-[#1A1918] list-decimal ml-5">
              {study.open_questions.map((q, i) => <li key={i} className="leading-relaxed">{q}</li>)}
            </ol>
          </section>
        )}

        {citations.length > 0 && (
          <section className="mt-12 pt-8 border-t border-[#D6D3C8]" data-testid="study-bibliography">
            <SectionLabel className="text-[#8C271E]">Bibliography</SectionLabel>
            <ol className="mt-4 space-y-2 text-sm text-[#5C5A56] list-decimal ml-5">
              {[...citations].sort((a, b) => (a.title || "").localeCompare(b.title || "")).map((c) => (
                <li key={c.id}>
                  {formatCitation(c)}
                  {c.url && <> — <a href={c.url} target="_blank" rel="noopener noreferrer" className="text-[#8C271E] hover:underline">source</a></>}
                </li>
              ))}
            </ol>
          </section>
        )}

        {(related_items?.length > 0 || related_artifacts?.length > 0 || provenance_events?.length > 0) && (
          <section className="mt-12 pt-8 border-t border-[#D6D3C8]" data-testid="study-related">
            <SectionLabel className="text-[#8C271E]">Related Collection Records</SectionLabel>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              {related_items.map((r) => (
                <Link key={r.id} to={`/collection/item/${r.slug}`} className="group flex items-center gap-3 bg-white border border-[#D6D3C8] p-3 hover:border-[#5C5A56] transition-colors" data-testid={`study-related-item-${r.slug}`}>
                  <FramedImage src={r.images?.[0]?.url} alt={r.title} className="w-12 h-12 flex-shrink-0" />
                  <span className="min-w-0"><span className="block showcase-serif text-[15px] truncate group-hover:text-[#8C271E]">{r.title}</span><span className="block text-xs text-[#5C5A56] truncate">{r.category}</span></span>
                  <ChevronRight className="w-4 h-4 text-[#9A9790] ml-auto flex-shrink-0" />
                </Link>
              ))}
              {related_artifacts.map((a) => (
                <Link key={a.id} to={`/artifacts/${a.slug}`} className="group flex items-center justify-between gap-3 bg-white border border-[#D6D3C8] p-3 hover:border-[#5C5A56] transition-colors">
                  <span className="showcase-serif text-[15px] truncate group-hover:text-[#8C271E]">{a.name}</span>
                  <ChevronRight className="w-4 h-4 text-[#9A9790] flex-shrink-0" />
                </Link>
              ))}
            </div>
            {provenance_events?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {provenance_events.map((ev) => (
                  <span key={ev.id} className="text-xs border border-[#D6D3C8] px-2 py-1 text-[#5C5A56]">{ev.date_label} · {ev.title}</span>
                ))}
              </div>
            )}
          </section>
        )}

        {suggested?.length > 0 && (
          <section className="mt-12 pt-8 border-t border-[#D6D3C8]" data-testid="study-suggested">
            <SectionLabel className="text-[#8C271E]">Suggested Reading</SectionLabel>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              {suggested.map((s) => (
                <Link key={s.id} to={`/studies/${s.slug}`} className="group flex items-start gap-3 bg-white border border-[#D6D3C8] p-4 hover:border-[#8C271E] transition-colors" data-testid={`suggested-${s.slug}`}>
                  <BookOpen className="w-5 h-5 text-[#8C271E] flex-shrink-0 mt-0.5" />
                  <span>
                    <span className="block showcase-serif text-[15px] leading-snug group-hover:text-[#8C271E]">{s.title}</span>
                    {s.subtitle && <span className="block text-xs text-[#5C5A56] mt-1 line-clamp-2">{s.subtitle}</span>}
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {study.revision_history?.length > 0 && (
          <section className="mt-12 pt-8 border-t border-[#D6D3C8]" data-testid="study-revisions">
            <SectionLabel className="text-[#8C271E]">Revision History</SectionLabel>
            <ul className="mt-4 space-y-2 text-sm text-[#5C5A56]">
              {study.revision_history.map((r, i) => (
                <li key={i} className="flex gap-3">
                  <span className="tabular-nums text-[#1A1918] font-medium whitespace-nowrap">{r.date}</span>
                  <span>{r.note}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="mt-14">
          <Link to="/studies" className="inline-flex items-center gap-2 text-sm text-[#8C271E] hover:underline">
            More Historical Studies <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};
